import { z } from "zod";

/**
 * Hizmet ilişkisi şemaları (CLOS-246 `[133]`).
 *
 * Hukuk bürosu ile kanonik müvekkil (Müvekkil Alanı) arasındaki AÇIK KAPSAMLI hukuki
 * hizmet ilişkisi. Kartın çekirdek kuralı: hukuk bürosu kendisini TEK TARAFLI
 * yetkilendiremez — ilişki ancak müvekkil tarafının (müvekkilin kendisi ya da
 * `service_actions` kapsamlı aktif temsilci, CLOS-178) kararıyla aktifleşir.
 *
 * ⚠ Aktif hizmet ilişkisi KAYIT ERİŞİMİ DEĞİLDİR ve hiçbir aktöre temsil/onay/imza
 * yetkisi vermez (kartın bağımlılık sınırı). Sözleşme, not ve kişisel veri erişimi her
 * nesne için permission + kayıt erişimiyle AYRICA verilir.
 */

/** Kapsam türleri, süreler ve onay politikası sürümlü konfig (config/serviceRelationship.json). */
export const serviceRelationshipConfigSchema = z.object({
  version: z.number().int().positive(),
  scopeTypes: z.array(z.object({ key: z.string().min(1), label: z.string().min(1) })).min(1),
  defaultDurationDays: z.number().int().positive(),
  maxDurationDays: z.number().int().positive(),
  requestExpiryDays: z.number().int().positive(),
  /** Bilinmeyen politika açılışta REDDEDİLİR: kod desteklemediği kuralı taşıyormuş gibi yapamaz. */
  approvalPolicy: z.literal("single"),
  /** Yaşam döngüsü politikası (CLOS-248 `[134]`) — gerekçe sözlükleri + dönüş koşulu. */
  suspendReasons: z.array(z.object({ key: z.string().min(1), label: z.string().min(1) })).min(1),
  endReasons: z.array(z.object({ key: z.string().min(1), label: z.string().min(1) })).min(1),
  /** `suspender` = askıdan dönüşü yalnız askıyı koyan taraf başlatır; bilinmeyen değer reddedilir. */
  resumeApproval: z.literal("suspender"),
  /**
   * Büro değişimi politikası (CLOS-249 `[142]`): sona eren büroya yeni erişim verilmez,
   * açık işler OTOMATİK taşınmaz — bilinmeyen politika değeri açılışta reddedilir.
   */
  transitionPolicy: z.object({
    transitionGraceDays: z.number().int().positive(),
    endedFirmFutureAccess: z.literal("closed"),
    openWorkTransfer: z.literal("manual"),
  }),
});
export type ServiceRelationshipConfig = z.infer<typeof serviceRelationshipConfigSchema>;

export type ServiceRelationshipStatus =
  "pending" | "active" | "rejected" | "cancelled" | "suspended" | "ended";

/** Askı/sona erdirme komutlarının gövdesi — gerekçe konfig sözlüğünden (serviste doğrulanır). */
export const serviceRelationshipLifecycleSchema = z.strictObject({
  reason: z.string().trim().min(1, "Gerekçe zorunludur"),
});
export type ServiceRelationshipLifecycleInput = z.infer<typeof serviceRelationshipLifecycleSchema>;

/** Talebi hangi tarafın başlattığı — karar HEP karşı tarafta. */
export type ServiceRelationshipInitiator = "law_firm" | "client";

const scopeAlani = z.string().trim().min(1, "Hizmet kapsamı zorunludur");
const scopeDetayi = z
  .string()
  .trim()
  .max(1000, "Kapsam açıklaması en fazla 1000 karakter olabilir")
  .optional();
const sureAlani = z
  .number()
  .int("Süre tam sayı olmalıdır")
  .positive("Süre pozitif olmalıdır")
  .optional();

/**
 * ⚠ Hedef adres (K1 · CLOS-1105 · ADR-0067): karşı taraf E-POSTAYLA çağrılır, hangi
 * organizasyonun adına kabul edileceğini ALICI kendi oturumunda seçer. Gönderen, adresin
 * Closer'da kayıtlı olup olmadığını hiçbir yanıttan öğrenmez (R3 §21.2).
 */
const hedefEposta = z.string().trim().email("Geçerli bir e-posta girin").max(200);

/**
 * Hukuk bürosunun talebi — karar müvekkil tarafında.
 *
 * ⚠ İKİ YOL: `targetEmail` YOKSA müvekkil kendi CRM kaydının sponsorlu alanından çözülür
 * (eski yol); VARSA talep o adrese gider ve karşı organizasyon kabul anında belli olur
 * (CLOS-1107 — yerel müvekkil kaydını Closer'a bağlama daveti).
 */
export const createServiceRelationshipSchema = z.strictObject({
  scope: scopeAlani,
  scopeDetail: scopeDetayi,
  durationDays: sureAlani,
  targetEmail: hedefEposta.optional(),
});
export type CreateServiceRelationshipInput = z.infer<typeof createServiceRelationshipSchema>;

/**
 * Yerel müvekkili Closer'a DAVET formu (CLOS-1107/1116): büro talebiyle aynı gövde, adres
 * ZORUNLU — davet penceresi adressiz gönderilemez ve hata metni şemadan gelir.
 */
export const clientLinkInviteSchema = z.strictObject({
  scope: scopeAlani,
  scopeDetail: scopeDetayi,
  durationDays: sureAlani,
  targetEmail: hedefEposta,
});
export type ClientLinkInviteInput = z.infer<typeof clientLinkInviteSchema>;

export type ServiceRelationshipAddressedTo = "organization" | "email";

export type ClientSideAuthority =
  { kind: "self" } | { kind: "representative"; representationId: string };

export type ServiceRelationshipView = {
  id: string;
  clientId: string | null;
  /** ⚠ NULL: talep bir ADRESE gitti ve henüz kabul edilmedi (K1). */
  workspaceTenantId: string | null;
  /** Talebin gittiği adres; organizasyon hedefli talepte null. */
  targetEmail: string | null;
  addressedTo: ServiceRelationshipAddressedTo;
  initiatedBy: ServiceRelationshipInitiator;
  scope: string;
  scopeDetail: string | null;
  durationDays: number;
  status: ServiceRelationshipStatus;
  /**
   * ⚠ Askıyı KOYAN taraf görünümde: `resumeApproval=suspender` politikası dönüş hakkını
   * ona veriyor. Alan olmasaydı ekran, sunucunun reddedeceği bir düğmeyi herkese açardı —
   * müzakeredeki `decidingTenantId` ile aynı gerekçe.
   */
  suspendedBy: ServiceRelationshipInitiator | null;
  suspendReason: string | null;
  startsAt: string | null;
  endsAt: string | null;
  requestExpiresAt: string;
  createdByName: string;
  resolvedByName: string | null;
  resolvedAt: string | null;
  /** Son "yeniden gönder" anı (CLOS-1140 §12.3) — cooldown ve idempotent e-posta işi için. */
  renewedAt: string | null;
  createdAt: string;
};

export type WorkspaceServiceRequestView = {
  id: string;
  /** ⚠ NULL: büro e-postayla çağrıldı ve henüz kabul etmedi (K1); adı da yok, adresi var. */
  lawFirmTenantId: string | null;
  lawFirmName: string | null;
  /** Talebin gittiği adres; organizasyon hedefli talepte null. */
  targetEmail: string | null;
  addressedTo: ServiceRelationshipAddressedTo;
  initiatedBy: ServiceRelationshipInitiator;
  scope: string;
  scopeDetail: string | null;
  durationDays: number;
  status: ServiceRelationshipStatus;
  /** Askıyı KOYAN taraf — dönüş hakkı onda (büro görünümüyle aynı gerekçe). */
  suspendedBy: ServiceRelationshipInitiator | null;
  suspendReason: string | null;
  startsAt: string | null;
  endsAt: string | null;
  requestExpiresAt: string;
  createdByName: string;
  createdAt: string;
};

/** Ekranda görünen durum adları — metin şemanın yanında (bkz. temsil daveti şemaları). */
export const SERVICE_RELATIONSHIP_STATUS_NAMES: Record<ServiceRelationshipStatus, string> = {
  pending: "Karar bekliyor",
  active: "Aktif",
  rejected: "Reddedildi",
  cancelled: "Geri çekildi",
  suspended: "Askıda",
  ended: "Sona erdi",
};

/** Talebi kimin başlattığı — karar HEP karşı tarafta olduğu için ekranda ayırt ediliyor. */
export const SERVICE_RELATIONSHIP_INITIATOR_NAMES: Record<ServiceRelationshipInitiator, string> = {
  law_firm: "Büro talebi",
  client: "Müvekkil talebi",
};
